import type { FormatNumberOptions, IntlShape } from 'react-intl';
import { formatTimeHhMmSs } from './dateTimeFormatters';
import { getConvertedDistance, getConvertedElevation, metersToKilometers, metersToMiles } from './distanceConverters';
import { getUnitAbbreviation, wrapFormattedPartsUnitInSpan } from './utils';

/**
 * Converts meters to the appropriate unit and wraps the unit in a span so it can be styled differently than the numeric value
 * @param intl Intl object
 * @param meters Meters value
 * @param metric Use km instead of mi
 * @param opts `formatNumberToParts` options
 * @param unitSpace Include a space before the unit
 * @returns A fragment with the converted distance value and the unit, wrapped in a span
 */
export const getFormattedDistanceWithUnitInSpan = (
  intl: IntlShape,
  meters: number,
  metric: boolean,
  opts?: FormatNumberOptions,
  unitSpace?: boolean
) =>
  wrapFormattedPartsUnitInSpan(
    intl.formatNumberToParts(getConvertedDistance(meters, metric), {
      style: 'unit',
      unit: metric ? 'kilometer' : 'mile',
      notation: 'compact',
      maximumFractionDigits: 1,
      ...opts
    }),
    unitSpace
  );

/**
 * Converts meters to the appropriate unit
 * @param intl Intl object
 * @param meters Meters value
 * @param metric Use km instead of mi
 * @param opts `formatNumberToParts` options
 * @returns The converted distance value and unit
 */
export const getFormattedDistance = (intl: IntlShape, meters: number, metric: boolean, opts?: FormatNumberOptions) =>
  intl.formatNumber(getConvertedDistance(meters, metric), {
    style: 'unit',
    unit: metric ? 'kilometer' : 'mile',
    notation: 'compact',
    maximumFractionDigits: 1,
    ...opts
  });

/**
 * Converts meters to the appropriate unit and wraps the unit in a span so it can be styled differently than the numeric value
 * @param intl Intl object
 * @param meters Meters value
 * @param metric Leave as m instead of ft
 * @param unitSpace Include a space before the unit
 * @returns A fragment with the converted distance value and the unit, wrapped in a span
 */
export const getFormattedElevationWithUnitInSpan = (intl: IntlShape, meters: number, metric: boolean, unitSpace?: boolean) =>
  wrapFormattedPartsUnitInSpan(
    intl.formatNumberToParts(getConvertedElevation(meters, metric), {
      style: 'unit',
      unit: metric ? 'meter' : 'foot',
      maximumFractionDigits: 0
    }),
    unitSpace
  );

/**
 * Converts meters to the appropriate unit
 * @param intl Intl object
 * @param meters Meters value
 * @param metric Leave as m instead of ft
 * @returns The converted elevation value and unit
 */
export const getFormattedElevation = (intl: IntlShape, meters: number, metric: boolean) =>
  intl.formatNumber(getConvertedElevation(meters, metric), {
    style: 'unit',
    unit: metric ? 'meter' : 'foot',
    maximumFractionDigits: 0
  });

/**
 * Calculates pace and wraps the unit in a span so it can be styled differently than the numeric value
 * @param intl Intl object
 * @param meters Meters value
 * @param seconds Seconds value
 * @returns A fragment with the converted pace and the unit, wrapped in a span
 */
export const getFormattedPaceWithUnitInSpan = (intl: IntlShape, meters: number, seconds: number, metric: boolean) => {
  const distance = metric ? metersToKilometers(meters) : metersToMiles(meters);
  const secondsPerUnitDistance = seconds / distance;
  return (
    <>
      {formatTimeHhMmSs(secondsPerUnitDistance)}
      <span>/{getUnitAbbreviation(intl, metric ? 'kilometer' : 'mile')}</span>
    </>
  );
};
